import {
  faClose,
  faSave
} from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Card, Form, Input, InputNumber, message, Popconfirm, Spin } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGoBack } from "../customHooks/useGoBack";
import { validateNonNegativeNumber } from "../helpers/common";
import ForwarderService from "../services/Forwarder/ForwarderService";
import Title from "antd/es/typography/Title";
import AppButton from "../common/AppButton";
import ConfirmDeleteButton from "../common/actions/ConfirmDeleteButton";

const ForwarderDetails = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const { forwarderID, pageMode } = location.state || {};
  const goBack = useGoBack();

  useEffect(() => {
    if (pageMode !== "edit") {
      setLoading(false);
      return;
    }
    ForwarderService
      .getForwarderDetails(forwarderID)
      .then((response) => {
        if (response.status === 200) {
          form.setFieldsValue(response.data);
        }
      })
      .catch((err) => {
        console.error(err);
        message.error("Failed to load forwarder details");
      })
      .finally(() => setLoading(false));
  }, [forwarderID, form]);

  const onFinish = async (values) => {
    setLoading(true);
    values = await form.validateFields();
    if (pageMode == "edit") {
      ForwarderService
        .updateForwarder({ forwarderID, ...values })
        .then(() => {
          message.success("Forwarder updated successfully");
          goBack();
        })
        .catch((err) => {
          console.error(err);
          message.error("Failed to update forwarder");
        })
        .finally(() => setLoading(false));
    } else {
      ForwarderService.createForwarder(values)
        .then((response) => {
          message.success("Forwarder saved successfully!");
          form.resetFields();
          goBack();
        })
        .catch((error) => {
          console.error(error);
          message.error("Error saving forwarder.");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleClose = () => {
    goBack();
  };

  const handleDelete = () => {
    setLoading(true);
    ForwarderService.deleteForwarder(forwarderID)
      .then(() => {
        message.success("Forwarder deleted successfully");
        goBack();
      })
      .catch((err) => {
        console.error(err);
        message.error("Failed to delete forwarder");
      })
      .finally(() => setLoading(false));
  };

  return (
    <Form form={form} layout="vertical" className="px-8 relative mb-4" initialValues={{
      detentionFreeDays: 0,
      detentionDailyFee: 0,
    }}>
      <div className="flex justify-start items-center w-full my-2  mx-2 font-inter">
        <span
          className="!font-medium text-[30px] !text-[#015373] !m-0 pl-4 mt-10"
        >
          {pageMode === "edit" ? "Freight Forwarder Details" : "New Freight Forwarder"}
        </span>
        <div className=" flex items-center justify-end pr-4 pt-2 h-full">
          <Button
            type="link"
            onClick={goBack}
            className="text-md font-medium !text-[#01A4A6] "
          >
            / Freight Forwarder
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center items-center h-48">
          <Spin size="large" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <Card className="pb-1 rounded-3xl shadow-xl bg-white bg-opacity-80 grid grid-cols-1 col-span-3">
            <div className=" mt-2 grid grid-cols-1 lg:grid-cols-2 gap-y-0 gap-x-16 ml-10 mr-10">

              {/* <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{
                  detentionFreeDays: 0,
                  detentionDailyFee: 0,
                }}
              > */}
              <Form.Item
                name="companyName"
                label="Company Name"
                rules={[{ required: true, message: "Required" }]}
                className="col-span-2"
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="companyShortName"
                label="Short Code"
                rules={[
                  { required: true, message: "Required" },
                  { max: 10, message: "Short Code must be at most 10 characters" }
                ]}
              >
                <Input maxLength={10} />
              </Form.Item>

              <Form.Item name="contactName" label="Contact Name" rules={[{ required: true, message: "Required" }]}>
                <Input />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[{ required: true, type: "email", message: "Invalid email" }]}
                className="col-span-2"
              >
                <Input />
              </Form.Item>
            </div>
          </Card>
          <div className="col-span-2 flex flex-col justify-between">
            <Card className="pb-1 rounded-3xl shadow-xl bg-white bg-opacity-80 col-span-2 h-fit">
              <div className="grid grid-cols-2 gap-y-4 gap-x-16 ml-10 mr-10">
                <Form.Item
                  label="Free Days"
                  name="detentionFreeDays"
                  rules={[{ validator: validateNonNegativeNumber(false), required: true }]}
                  className="w-full"
                >
                  <InputNumber
                    controls={false}
                    placeholder="Days"
                    size="large"
                    className="w-full"
                    min={0}
                    onKeyDown={(e) => {
                      const allowedKeys = [
                        "Backspace", "Delete", "ArrowLeft", "ArrowRight",
                        "Tab", "Home", "End"
                      ];
                      const isNumberKey = e.key >= "0" && e.key <= "9";
                      const isAllowed = isNumberKey || allowedKeys.includes(e.key);
                      if (!isAllowed) e.preventDefault();
                    }}
                  />
                </Form.Item>

                <Form.Item
                  label="Daily Fee"
                  name="detentionDailyFee"
                  rules={[{ validator: validateNonNegativeNumber(false), required: true }]}
                  className="w-full"
                >
                  <InputNumber
                    controls={false}
                    placeholder="Fee"
                    size="large"
                    className="w-full"
                    min={0}
                    precision={2}
                    onKeyDown={(e) => {
                      const allowedKeys = [
                        "Backspace", "Delete", "ArrowLeft", "ArrowRight",
                        "Tab", "Home", "End"
                      ];
                      const isNumberKey = e.key >= "0" && e.key <= "9";
                      if (e.key === ".") {
                        if (e.currentTarget.value.includes(".")) e.preventDefault();
                        return;
                      }
                      const isAllowed = isNumberKey || allowedKeys.includes(e.key);
                      if (!isAllowed) e.preventDefault();
                    }}
                  />
                </Form.Item>
              </div>
            </Card>
            <div className="flex justify-end">
              <AppButton variant="save" onClick={onFinish}>
                Save
              </AppButton>
              {pageMode === "edit" ? (<ConfirmDeleteButton
                title={
                  <>
                    {`You are about to delete ${form.getFieldValue("companyName")} forwarder.`}
                    <br />
                    Please confirm you wish to proceed.
                  </>
                }
                onConfirm={handleDelete}
              />) : null}
              <AppButton
                variant="close"
                onClick={handleClose}>
                  Close
                </AppButton>
            </div>
          </div>
        </div>
      )}
    </Form>
  );
};

export default ForwarderDetails;
